import { countryOptions, hintStates, isRequired, liveTransform, resolveText, type FieldSpec } from "@gsignatures/core";
import { useLayoutEffect, useRef, useState, type ChangeEvent } from "react";
import { Icon } from "../components/Icon";
import type { Answers } from "../state/answers";
import { S } from "../strings";

const COUNTRIES = countryOptions(["IN"]);

/** Short inputs sit two to a row on wider screens. */
export function isShort(spec: FieldSpec): boolean {
  if (spec.type === "country" || spec.type === "number" || spec.type === "date" || spec.type === "select") return true;
  if (spec.inputMode === "numeric" || spec.inputMode === "decimal") return true;
  return ["address-level1", "address-level2", "postal-code"].includes(spec.autocomplete ?? "");
}

export const fieldDomId = (id: string) => `f-${id}`;

/** One question, built from its spec: label, help, input, live format chips, and the message. */
export function Field({ spec, a, issuer }: { spec: FieldSpec; a: Answers; issuer: string }) {
  const { values } = a;
  const id = fieldDomId(spec.id);
  const msgId = `${id}-msg`;
  const hintsId = `${id}-hints`;
  const [hasFocus, setHasFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (caret.current === null || !el || document.activeElement !== el) return;
    el.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  const label = resolveText(spec.label, values);
  const help = resolveText(spec.help, values);
  const required = isRequired(spec, values, a.ctx);
  const line = spec.meta?.line !== undefined ? String(spec.meta.line) : null;
  const issues = (a.validation.byField[spec.id] ?? []).filter((i) => i.severity !== "flag");
  const issue = issues.find((i) => i.severity === "error") ?? issues.find((i) => i.severity === "warning");
  const touched = a.touched[spec.id];
  // Reward early, punish late: a fixed answer clears at once; a new error waits until the person leaves the field.
  const show = !!touched && !!issue && !(hasFocus && issue.severity === "error" && touched === "ok");
  const isErr = show && issue?.severity === "error";
  const isWarn = show && issue?.severity === "warning";
  const raw = values[spec.id];
  const text = typeof raw === "string" ? raw : "";
  const good = !!touched && text.trim() !== "" && !issue && !hasFocus;
  const hints = hintStates(spec, values);
  const describedBy = [msgId, hints.length ? hintsId : null].filter(Boolean).join(" ");

  const onFocus = () => {
    setHasFocus(true);
    a.setFocus(spec.id);
  };
  const onBlur = () => {
    setHasFocus(false);
    a.commit(spec.id);
  };
  const onText = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const el = e.target;
    const before = el.value;
    const pos = el.selectionStart ?? before.length;
    const v = liveTransform(spec, before);
    if (v !== before) caret.current = Math.max(0, Math.min(v.length, pos + (v.length - before.length)));
    a.type(spec.id, v);
  };

  const tags = (
    <>
      {!required && <span className="opt">{S.common.optional}</span>}{" "}
      {line && (
        <span className="line-tag" title={S.common.lineTitle(issuer)}>
          {S.common.lineTag(line)}
        </span>
      )}
    </>
  );

  if (spec.type === "toggle") {
    const on = raw === undefined || raw === null ? spec.defaultValue === true : raw === true;
    return (
      <div className="field">
        <label className="toggle" htmlFor={id}>
          <input
            type="checkbox"
            role="switch"
            id={id}
            checked={on}
            aria-describedby={help ? msgId : undefined}
            onFocus={() => a.setFocus(spec.id)}
            onChange={(e) => a.commit(spec.id, e.target.checked)}
          />
          <span className="toggle-label">{label}</span>{" "}
          {line && (
            <span className="line-tag" title={S.common.lineTitle(issuer)}>
              {S.common.lineTag(line)}
            </span>
          )}
        </label>
        {help && (
          <div id={msgId} className="help">
            {help}
          </div>
        )}
      </div>
    );
  }

  const cls = `input ${spec.transforms?.includes("upper") ? "caps" : ""} ${isErr ? "bad" : isWarn ? "warn" : good ? "good" : ""}`;
  const common = {
    id,
    "aria-describedby": describedBy,
    "aria-invalid": isErr ? (true as const) : undefined,
    "aria-required": required ? (true as const) : undefined,
    onFocus,
    onBlur,
  };

  let control;
  if (spec.type === "country" || spec.type === "select") {
    const opts = spec.type === "country" ? COUNTRIES : (spec.options ?? []);
    const known = opts.some((o) => o.value === text);
    control = (
      <div className="select-wrap">
        <select
          {...common}
          className={`select ${isErr ? "bad" : ""}`}
          value={text}
          autoComplete={spec.autocomplete ?? (spec.type === "country" ? "country" : undefined)}
          onChange={(e) => a.commit(spec.id, e.target.value)}
        >
          <option value="">{spec.type === "country" ? S.tax.chooseCountry : S.tax.choose}</option>
          {!known && text && <option value={text}>{text}</option>}
          {opts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Icon n="down" className="select-chev" />
      </div>
    );
  } else if (spec.type === "textarea") {
    control = (
      <textarea
        {...common}
        ref={(el) => {
          inputRef.current = el;
        }}
        className={`${cls} textarea`}
        rows={2}
        value={text}
        placeholder={spec.placeholder}
        spellCheck={false}
        onChange={onText}
      />
    );
  } else {
    control = (
      <input
        {...common}
        ref={(el) => {
          inputRef.current = el;
        }}
        className={cls}
        type="text"
        value={text}
        placeholder={spec.placeholder}
        inputMode={spec.inputMode ?? (spec.type === "number" ? "decimal" : spec.type === "date" ? "numeric" : "text")}
        autoComplete={spec.autocomplete ?? "off"}
        autoCapitalize={spec.transforms?.includes("upper") ? "characters" : spec.sensitive ? "off" : undefined}
        spellCheck={false}
        onChange={onText}
      />
    );
  }

  return (
    <div className="field">
      <label htmlFor={id}>
        <span>{label}</span> {tags}
      </label>
      {control}
      {hints.length > 0 && (
        <ul className="chips" id={hintsId} aria-label={S.tax.hintsLabel}>
          {hints.map((h) => (
            <li key={h.id} className={`chip ${h.ok ? "ok" : ""}`}>
              {h.ok && <Icon n="check" />}
              {h.label}
              <span className="sr">: {h.ok ? S.tax.hintDone : S.tax.hintTodo}</span>
            </li>
          ))}
        </ul>
      )}
      <div id={msgId} aria-live="polite">
        {show && issue ? (
          <div className={isErr ? "err" : "warn-text"}>
            <Icon n={isErr ? "alert" : "info"} />
            <span>{issue.message}</span>
          </div>
        ) : help ? (
          <div className="help">{help}</div>
        ) : null}
      </div>
    </div>
  );
}
